import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardBody, CardFooter } from '@/components/ui/Card';

export function LoginForm() {
  const { login, isLoading } = useAuth();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ userId?: string; password?: string }>({});

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!userId.trim()) {
      newErrors.userId = 'Username or email is required';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await login({ userId: userId.trim(), password });
    } catch {
      // Error is handled by AuthContext
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader
        title="Welcome back"
        description="Sign in to your account to continue"
      />
      <form onSubmit={handleSubmit}>
        <CardBody className="space-y-4">
          <Input
            id="login-user-id"
            label="Username or Email"
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            error={errors.userId}
            leftIcon={<User className="h-4 w-4" />}
            placeholder="Enter your username or email"
            autoComplete="username"
            disabled={isLoading}
            data-testid="login-user-id"
          />
          <Input
            id="login-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            leftIcon={<Lock className="h-4 w-4" />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="focus:outline-none"
                tabIndex={-1}
                data-testid="login-toggle-password-visibility"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            }
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isLoading}
            data-testid="login-password"
          />
        </CardBody>
        <CardFooter className="flex-col gap-4">
          <Button
            type="submit"
            className="w-full"
            isLoading={isLoading}
            data-testid="login-submit"
          >
            Sign in
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
